import React, { useState, useEffect } from 'react';
import { getSubscriptionInvoices, cancelSubscription, getSubscriptionStatus, updatePaymentMethod } from '../Services/SubscriptionApi';
import './Settings.css';

const BillingInfo = () => {
    const [loading, setLoading] = useState(true);
    const [invoices, setInvoices] = useState([]);
    const [subscriptionStatus, setSubscriptionStatus] = useState(null);
    const [showCancelConfirm, setShowCancelConfirm] = useState(false);
    const [cancelLoading, setCancelLoading] = useState(false);
    const [cancelError, setCancelError] = useState(null);

    useEffect(() => {
        fetchBillingData();
    }, []);

    const fetchBillingData = async () => {
        try {
            setLoading(true);
            const [invoiceRes, statusRes] = await Promise.all([
                getSubscriptionInvoices(),
                getSubscriptionStatus()
            ]);
            setInvoices(invoiceRes ?? []);
            setSubscriptionStatus(statusRes);
        } catch (error) {
            console.error("Failed to fetch billing data:", error);
        } finally {
            setLoading(false);
        }
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return '-';
        return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    };

    const formatAmount = (amount) => {
        if (amount == null) return '-';
        return `${(amount / 100).toFixed(2)}`;
    };

    const getBillingPeriod = (planName) => {
        if (!planName) return '';
        const lower = planName.toLowerCase();
        if (lower.includes('monthly')) return '/mo';
        if (lower.includes('yearly')) return '/yr';
        return '';
    };

    const handleCancelSubscription = async () => {
        try {
            setCancelLoading(true);
            setCancelError(null);
            await cancelSubscription();
            setShowCancelConfirm(false);
            await fetchBillingData();
        } catch (error) {
            console.error("Failed to cancel subscription:", error);
            setCancelError(error.message || "Failed to cancel subscription. Please try again.");
        } finally {
            setCancelLoading(false);
        }
    };

    const isCancelling = subscriptionStatus?.status === 'cancelling' || subscriptionStatus?.status === 'canceled';

    const getStatusBadge = () => {
        if (!subscriptionStatus?.hasSubscription) return { label: 'No Plan', className: '' };
        switch (subscriptionStatus.status) {
            case 'trialing': return { label: 'Trialing', className: 'settings-plan-badge-trialing' };
            case 'active': return { label: 'Active', className: '' };
            case 'cancelling': return { label: 'Cancelling', className: 'settings-plan-badge-cancelling' };
            case 'canceled': return { label: 'Canceled', className: 'settings-plan-badge-cancelling' };
            case 'past_due': return { label: 'Past Due', className: 'settings-plan-badge-pastdue' };
            default: return { label: 'Active', className: '' };
        }
    };

    const getFooterText = () => {
        if (!subscriptionStatus) return '';
        if (isCancelling && subscriptionStatus.currentPeriodEnd) {
            return `Your subscription will end on ${formatDate(subscriptionStatus.currentPeriodEnd)}`;
        }
        if (subscriptionStatus.status === 'trialing' && subscriptionStatus.trialEnd) {
            return `Trial ends on ${formatDate(subscriptionStatus.trialEnd)}`;
        }
        if (subscriptionStatus.currentPeriodEnd) {
            return `Renews on ${formatDate(subscriptionStatus.currentPeriodEnd)}`;
        }
        return '';
    };

    const getStatusIcon = () => {
        if (!subscriptionStatus?.hasSubscription) return '📦';
        switch (subscriptionStatus.status) {
            case 'trialing': return '⏱';
            case 'active': return '✓';
            case 'cancelling':
            case 'canceled': return '✕';
            case 'past_due': return '⚠';
            default: return '✓';
        }
    };

    const badge = getStatusBadge();

    if (loading) {
        return (
            <div className="subscription-loading" style={{ flex: 1, justifyContent: 'center' }}>
                <div className="subscription-spinner"></div>
                <span>Loading Billing Info...</span>
            </div>
        );
    }

    return (
        <>
            {subscriptionStatus?.message && (
                <div className={`settings-status-card settings-status-${subscriptionStatus.status}`}>
                    <div className="settings-status-icon">{getStatusIcon()}</div>
                    <div className="settings-status-content">
                        <span className="settings-status-message">{subscriptionStatus.message}</span>
                    </div>
                    {subscriptionStatus.status === 'past_due' && (
                        <button
                            type="button"
                            className="settings-status-update-btn"
                            onClick={async () => {
                                try {
                                    const data = await updatePaymentMethod();
                                    if (data?.url) {
                                        window.location.href = data.url;
                                    }
                                } catch (error) {
                                    console.error("Failed to open payment update:", error);
                                }
                            }}
                        >
                            Update Payment Method
                        </button>
                    )}
                </div>
            )}

            {subscriptionStatus?.hasSubscription && (
                <div className="settings-plan-card">
                    <div className="settings-plan-card-header">
                        <div className={`settings-plan-badge ${badge.className}`}>
                            {badge.label}
                        </div>
                        <span className="settings-plan-card-title">Your Plan</span>
                    </div>
                    <div className="settings-plan-card-body">
                        <div className="settings-plan-detail">
                            <div className="settings-plan-icon">⚡</div>
                            <div className="settings-plan-detail-text">
                                <span className="settings-plan-name">{subscriptionStatus.planName}</span>
                                <span className="settings-plan-tier">{subscriptionStatus.currency?.toUpperCase()} Plan</span>
                            </div>
                        </div>
                        <div className="settings-plan-price">
                            <span className="settings-plan-amount">${formatAmount(subscriptionStatus.amount)}</span>
                            <span className="settings-plan-period">{getBillingPeriod(subscriptionStatus.planName)}</span>
                        </div>
                    </div>
                    <div className="settings-plan-card-footer">
                        <div className="settings-plan-renew-icon">📅</div>
                        <span className="settings-plan-renew-text">{getFooterText()}</span>
                    </div>
                </div>
            )}

            <div className="settings-invoices-card">
                <div className="settings-invoices-header">
                    <span className="settings-invoices-title">Invoices</span>
                </div>
                {invoices.length === 0 ? (
                    <div className="settings-invoices-empty">No invoices found</div>
                ) : (
                    <div className="settings-invoices-table-wrapper">
                        <table className="settings-invoices-table">
                            <thead>
                                <tr>
                                    <th>Date</th>
                                    <th>Plan</th>
                                    <th>Amount</th>
                                    <th>Status</th>
                                    <th>Billing Reason</th>
                                    <th></th>
                                </tr>
                            </thead>
                            <tbody>
                                {invoices.map((inv) => (
                                    <tr key={inv.id}>
                                        <td>{formatDate(inv.created_at)}</td>
                                        <td>{inv.plan_name || '-'}</td>
                                        <td>{formatAmount(inv.amount_paid)}</td>
                                        <td>
                                            <span className={`settings-invoice-status ${inv.amount_paid > 0 ? 'settings-invoice-status-paid' : 'settings-invoice-status-trial'}`}>
                                                {inv.amount_paid > 0 ? 'Paid' : 'Trialing'}
                                            </span>
                                        </td>
                                        <td>{inv.billing_reason?.replaceAll(/_/g, ' ') || '-'}</td>
                                        <td>
                                            {inv.hosted_invoice_url && (
                                                <a
                                                    href={inv.hosted_invoice_url}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="settings-invoice-receipt-link"
                                                >
                                                    View Receipt
                                                </a>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {subscriptionStatus?.hasSubscription && !isCancelling && (
                <div className="settings-cancel-card">
                    <div className="settings-cancel-card-left">
                        <div className="settings-cancel-icon">⚠</div>
                        <div className="settings-cancel-info">
                            <span className="settings-cancel-title">Cancel Subscription</span>
                            <span className="settings-cancel-desc">
                                Your access will continue until the end of your current billing period. You won't be charged again.
                            </span>
                        </div>
                    </div>
                    <button
                        type="button"
                        className="settings-cancel-btn"
                        onClick={() => setShowCancelConfirm(true)}
                    >
                        Cancel Subscription
                    </button>
                </div>
            )}

            {showCancelConfirm && (
                <div
                    className="settings-overlay"
                    onClick={(e) => {
                        if (e.target === e.currentTarget) setShowCancelConfirm(false);
                    }}
                    onKeyDown={(e) => {
                        if (e.key === 'Escape') setShowCancelConfirm(false);
                    }}
                    role="dialog"
                    aria-modal="true"
                    aria-label="Cancel subscription confirmation"
                    tabIndex={-1}
                >
                    <div className="settings-dialog">
                        <div className="settings-dialog-icon">⚠</div>
                        <h3 className="settings-dialog-title">Cancel Subscription?</h3>
                        <p className="settings-dialog-text">
                            Are you sure you want to cancel? You'll still have access until the end of your current billing period.
                        </p>
                        {cancelError && (
                            <div className="settings-dialog-error">{cancelError}</div>
                        )}
                        <div className="settings-dialog-actions">
                            <button
                                type="button"
                                className="settings-dialog-btn-keep"
                                onClick={() => setShowCancelConfirm(false)}
                                disabled={cancelLoading}
                            >
                                Keep Subscription
                            </button>
                            <button
                                type="button"
                                className="settings-dialog-btn-cancel"
                                onClick={handleCancelSubscription}
                                disabled={cancelLoading}
                            >
                                {cancelLoading ? 'Cancelling...' : 'Yes, Cancel'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default BillingInfo;
